import { Component, DataSource, DataSourceRemoteDataChangeEvent, DataSourceSubscriberErrorEvent, Image, Layout, ModelDataChangeType, PanelProps, SelectionEvent, SelectionListener, Tab, Table } from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { MultiProfilePlanningTabs } from "../MultiProfilePlanningTabs";
import { OrderPlanningSplitView, OrderPlanningSplitViewDisplayMode } from "./OrderPlanningSplitView";
import { AutogenLayoutOperationsManagementMultiProfile } from "./autogen/AutogenLayoutOperationsManagementMultiProfile";
import { Logger, LogManager, MixinUtil, ModelRow, ObjectUtil } from "@mcleod/core";
import { OrderPlanningLayout } from "../OrderPlanningLayout";
import { RowOrderPlanningProfile } from "../../models/ModelOrderPlanningProfile";
import { OrderPlanningToolPanel } from "../OrderPlanningToolPanel";
import { ModelDataChange } from "@mcleod/components/src/databinding/DataSource";
import { OrderPlanningTimeline } from "../timeline/OrderPlanningTimeline";

const log = LogManager.getLogger("lme.display.planning.opsmgmt.OperationsManagementMultiProfile");
const timelineLiveUpdateFields = [
    "driver1_name", "tractor", "movement_id", "trailer_id", "trailer2", "trailer3", "dolly1", "dolly2",
    "last_loc_citystate", "tracking_late_string", "tracking_late_minutes", "mc_message_count",
    "np_stop_eta_time", "next_stop_eta", "next_stop_eta_late", "next_stop_eta_oor", "next_stop_late",
    "origin_detention", "destination_detention", "next_stop_detention", "next_stop_order_id",
    "stop_detention_statuses"
];

export class OperationsManagementMultiProfile extends AutogenLayoutOperationsManagementMultiProfile {
    private userProfiles: string[] = [];

    getThisAsLayout(): Layout {
        return this;
    }

    getTable(): Table {
        return null;
    }

    getToolsPanel(): OrderPlanningToolPanel {
        return null;
    }

    getLog(): Logger {
        return log;
    }

    onLoad(): void | Promise<void> {
        if (LMEUserSettings.get()?.order_planning_profile != null)
            this.userProfiles.push(LMEUserSettings.get().order_planning_profile);
        if (LMEUserSettings.get()?.order_planning_profile2 != null)
            this.userProfiles.push(LMEUserSettings.get().order_planning_profile2);
        if (LMEUserSettings.get()?.order_planning_profile3 != null)
            this.userProfiles.push(LMEUserSettings.get().order_planning_profile3);
        if (LMEUserSettings.get()?.order_planning_profile4 != null)
            this.userProfiles.push(LMEUserSettings.get().order_planning_profile4);

        const multiProfile = new MultiProfilePlanningTabs({
            tabset: this.tabsetOrderPlanningProfiles,
            profileDataSource: this.sourceOrderPlanningProfile,
            mainLayoutName: "lme/dispatch/planning/opsmgmt/OrderPlanningSplitView",
            advancedFilterLayout: "lme/dispatch/planning/OrderPlanningProfileFilter",
            startingProfiles: this.userProfiles,
            profileDescrFieldName: "title"
        });


        if (this.userProfiles.length > 0) {
            this.loadProfile(this.userProfiles[0]).then(() => {
                const profile = this.profile as RowOrderPlanningProfile;
                this.getSubscriptions(profile);
            });
        } else {
            const selectionListener:SelectionListener = (event: SelectionEvent) => {
                this.loadProfile(multiProfile.userProfiles[0]).then(() => {
                    const profile = this.profile as RowOrderPlanningProfile;
                    this.getSubscriptions(profile);
                });
            }
            selectionListener.runsOnce = true;
            this.tabsetOrderPlanningProfiles.addAfterTabSelectionListener(selectionListener);
        }


        this.tabsetOrderPlanningProfiles.addAfterTabSelectionListener((event: SelectionEvent) => {
            (event.newSelection as Tab).components.forEach((component: Component) => {
                if (component instanceof  OrderPlanningSplitView) {
                    let image:Image;
                    switch (component.displayMode?.dbValue) {
                        case "V":
                            image = this.imageOrdersEquipmentHorizontal;
                            break;
                        case "H":
                            image = this.imageOrdersEquipmentVertical;
                            break;
                        case "C":
                            image = this.imageOrdersTimelineVertical;
                            break;
                    }
                    if (!image) return;
                    this.updateImageColors(image);
                }
            });
        });
    }

    private async getSubscriptions(profile: RowOrderPlanningProfile) {
        this.sourceAvailableMove.search({profile_row: profile, is_dummy: true}).then(() => this.sourceAvailableMove.clear());

        profile.set("tractor_or_driver", "T");
        this.sourceEquipMoveTractor.search({profile_row: profile, is_dummy: true}).then(() => this.sourceEquipMoveTractor.clear());

        profile.set("tractor_or_driver", "D");
        this.sourceEquipMoveDriver.search({profile_row: profile, is_dummy: true}).then(() => this.sourceEquipMoveDriver.clear());
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        event.preventDefault();
        this.tabsetOrderPlanningProfiles.components.forEach((tab: Tab, index) => {
            tab.components.forEach((component: Component) => {
                if (component instanceof OrderPlanningSplitView) {
                    if ("lme/dispatch/available-move" === (event.target as DataSource).url) {
                        const dataSource = component.layoutOrderPlanningOrders.sourceAvailableMove
                        dataSource.handleDataChange({ type: event.change.type, data: event.change.data, lastUpdateNanos: event.change.lastUpdateNanos });
                        return;
                    } else if ("lme/dispatch/equip-move" === (event.target as DataSource).url) {
                        const equipSource = component.layoutOrderPlanningEquipment.sourceEquipMove;
                        const timeLayout = component.layoutTimeline;
                        const timeSource = timeLayout?.sourceEquipMove;
                        const eventSource = event.change.data.getString("source_type");

                        const profile = component.layoutOrderPlanningEquipment.profile as RowOrderPlanningProfile;
                        const showAvailable = profile.getBoolean("avail_tractor", false);
                        if ((eventSource == null && event.change.type === ModelDataChangeType.DELETE)
                            || (["D", "T"].includes(eventSource))
                            || ("A" === eventSource && showAvailable)) {
                            const eventCopy = { type: event.change.type, data: event.change.data, lastUpdateNanos: event.change.lastUpdateNanos }
                            equipSource.handleDataChange(eventCopy);
                            if ("A" !== eventSource && timeSource && this.shouldUpdateTimeline(timeLayout, timeSource, eventCopy)) {
                                timeSource?.handleDataChange(eventCopy);
                            }
                            return;
                        }
                    } else {
                        log.info("Unable to find applicable datasource to update.", event);
                    }
                }
            })
        })
    }

    onSubscriberError(event: DataSourceSubscriberErrorEvent) {
        this.handleSubscriberError(event);
    }

    // Override the main router's padding to get the vertical profile tabset to be flush with the edge of the screen
    getRouterProps(): Partial<PanelProps> {
        return { padding: 0 };
    }

    imageOrdersEquipmentHorizontalOnClick() {
        this.updateImageColors(this.imageOrdersEquipmentHorizontal);
        this.updateTab(this.tabsetOrderPlanningProfiles.getActiveTab(), OrderPlanningSplitView.ORDERS_EQUIPMENT_HORIZONTAL);
    }

    imageOrdersEquipmentVerticalOnClick() {
        this.updateImageColors(this.imageOrdersEquipmentVertical);
        this.updateTab(this.tabsetOrderPlanningProfiles.getActiveTab(), OrderPlanningSplitView.ORDERS_EQUIPMENT_VERTICAL);
    }

    imageOrdersTimelineVerticalOnClick() {
        this.updateImageColors(this.imageOrdersTimelineVertical);
        this.updateTab(this.tabsetOrderPlanningProfiles.getActiveTab(), OrderPlanningSplitView.ORDERS_TIMELINE_VERTICAL);
    }

    public updateImageColors(clickedImage: Image) {
        [ this.imageOrdersEquipmentHorizontal, this.imageOrdersEquipmentVertical, this.imageOrdersTimelineVertical ].forEach(image => {
            image.color = image === clickedImage ? "primary" : "subtle";
        });
    }

    private updateTab(profileTab:Tab, displayMode: OrderPlanningSplitViewDisplayMode) {
        const doInTabSplitView = (tab: Tab) => tab.components.forEach((component: Component) => {
                if (component instanceof OrderPlanningSplitView) {
                    component.displayMode = displayMode;
                }
            });

        if (profileTab) {
            doInTabSplitView(profileTab);
        } else {
            this.tabsetOrderPlanningProfiles.components.forEach((tab: Tab) => {
                doInTabSplitView(tab);
            });
        }
    }

    private shouldUpdateTimeline(timeLayout: OrderPlanningTimeline, timeSource: DataSource, change: ModelDataChange): boolean {
        if (ModelDataChangeType.ADD == change.type || ModelDataChangeType.DELETE == change.type)
            return true;

        const timelineToolsFields = timeLayout.getToolsPanel().getUpdateFields();

        const getAllData = (modelRow: ModelRow) => {
            const timelineData = modelRow.get("timeline_data") ?? [];
            const driver1Events = modelRow.get("driver1_timeline_data") ?? [];
            const driver2Events = modelRow.get("driver2_timeline_data") ?? [];
            const ctaData = Object.fromEntries(
                Object.entries(modelRow.data).filter(([key]) => timelineToolsFields.includes(key))
            );
            return timelineData.concat(driver1Events).concat(driver2Events).concat(ctaData);
        }

        let update: boolean = false;
        const existingIndex = timeSource.findRowInData(change.data);
        if (existingIndex != -1) {
            const existingRow = timeSource.data[existingIndex];
            if (existingRow) {
                const originalTimelineData = getAllData(existingRow);
                const newTimelineData = getAllData(change.data);
                if (originalTimelineData?.length != newTimelineData?.length) {
                    update = true;
                }
                newTimelineData?.forEach(td => {
                    const found = originalTimelineData?.find(otd => otd.id === td.id);
                    if (!found || !ObjectUtil.deepEqual(found, td)) {
                        update = true;
                    }
                });

                const changedData = existingRow.compareWithData(change.data.data);

                if (timelineLiveUpdateFields.some(prop => changedData.hasOwnProperty(prop))) {
                    update = true;
                }
            }
        }
        return update;
    }

    // Override with empty method to satisfy the OrderPlanningLayout mixin...we don't want it to really search
    override async search(): Promise<any> {
    }
}
MixinUtil.applyMixin(OperationsManagementMultiProfile, OrderPlanningLayout);

// TypeScript interface declaration for mixin integration
export interface OperationsManagementMultiProfile extends OrderPlanningLayout {
}
